import * as React from 'react';
import {connect} from 'react-redux';
// @ts-expect-error
import type {ActionMeta} from 'react-select';

import * as Ramda from 'ramda';

import type {SelectComponentValue} from 'nutshell-core/types';
import {Users} from 'nutshell-core/users';

import {SelectInlineCreatable} from 'shells/select-inline/select-inline-creatable';

import type {EmailAddress as EmailAddressFragment} from 'nutshell-graphql-types';

import type {EmailComposerFormState} from '../sender/types';

import {createSelectOptionsFromEmailAddresses} from './create-select-options-from-email-addresses';
import {styleOptionForEntityType} from './style-option-for-entity-type';

import styles from './email-selection.module.css';

type Addresses = EmailComposerFormState['ccAddresses'];
type Address = Addresses[number];

type OwnProps = {
    selectedAddresses: Addresses;
    onChange: (newOptions: Addresses) => any;
    onFocus?: () => void;
    onBlur?: () => void;
    availableAddresses: EmailAddressFragment[];
    errorMessage?: string;
};

type StateProps = {
    userOptions: {
        id: string;
        name: string;
    }[];
};

type Props = OwnProps & StateProps;

// @ts-expect-error
function mapStateToProps(state): StateProps {
    return {
        // @ts-expect-error
        userOptions: Users.getUserEmailOptions(state),
    };
}

class EmailCcSelectionComponent extends React.PureComponent<Props> {
    scrollNode: HTMLElement | null | undefined;

    render() {
        const optionsForSelect = createSelectOptionsFromEmailAddresses(
            this.props.availableAddresses
        ).concat(this.props.userOptions);

        const selectedAddresses = formStateToOption(this.props.selectedAddresses);
        // TODO: move this to something that finds it once, not each render
        const portalTarget = document.querySelector('#email-subject-and-body-fields');

        return (
            <div id='email-cc-field'>
                <div
                    ref={(node) => {
                        this.scrollNode = node;
                    }}
                    className={
                        portalTarget
                            ? styles['email-selection--scrollable']
                            : styles['email-selection']
                    }
                >
                    <SelectInlineCreatable
                        errorMessage={this.props.errorMessage}
                        placeholder=''
                        noOptionsMessage={() => null}
                        tokenizedStyle='light'
                        separatorKeys={[',', ' ', ';', 'Spacebar']}
                        options={optionsForSelect}
                        value={selectedAddresses}
                        onChange={this.handleChange}
                        inlineLabel='Cc'
                        autoFocus={true}
                        onFocus={this.props.onFocus}
                        onBlur={this.props.onBlur}
                        isValidNewOption={this.validateNewOption}
                        // @ts-expect-error
                        formatCreateLabel={(input) => input}
                        filterOption={this.filterOption}
                        multi={true}
                        // @ts-expect-error
                        menuPortalTarget={portalTarget || undefined}
                        menuPortalStyles={
                            portalTarget
                                ? // @ts-expect-error
                                  (base) => ({
                                      ...base,
                                      left: 64,
                                      top: 0,
                                      zIndex: 10,
                                  })
                                : undefined
                        }
                        styles={{option: styleOptionForEntityType}}
                    />
                </div>
            </div>
        );
    }

    /*
     * Only show nutshell users in the dropdown once the user has typed
     * two or more characters.
     */
    filterOption = (option: any, input: string): boolean => {
        if (Ramda.includes(option.data, this.props.userOptions)) {
            return input.length > 1;
        }

        return true;
    };

    /*
     * Handler here to return the actual email address objects instead
     * of the select options. We need to pass that information
     * around so our parent component knows which email addresses (and
     * their associated entities) are selected, so we can apply the right
     * information to the potentially selected email template
     */
    handleChange = (selectedOptions: SelectComponentValue[], actionMeta: ActionMeta) => {
        const newSelectedAddresses = selectedOptions.map((option) => {
            const existingAddress = this.props.availableAddresses.find(
                (address) => address.value.address === option.id
            );

            return existingAddress
                ? existingAddressToFormState(existingAddress)
                : optionToFormState(option);
        });

        // If we've added a new option, make sure we scroll to the bottom and show it
        if (actionMeta.action === 'select-option') {
            setTimeout(() => {
                if (this.scrollNode) {
                    this.scrollNode.scrollTop = this.scrollNode.scrollHeight;
                }
            }, 0);
        }

        this.props.onChange(newSelectedAddresses);
    };

    validateNewOption = (inputValue: string): boolean => {
        return inputValue.includes('@');
    };
}

function optionToFormState(option: SelectComponentValue): Address {
    return {
        emailAddress: option.id,
        display: option.name,
    };
}

function existingAddressToFormState(address: EmailAddressFragment): Address {
    return {
        emailAddress: address.value.address,
        display: address.value.formatted,
    };
}

function formStateToOption(addresses: Addresses): SelectComponentValue[] {
    return addresses.map((address) => ({
        id: address.emailAddress,
        name: address.display,
    }));
}

// @ts-expect-error
export const EmailCcSelection = connect<Props, OwnProps, _, _, _, _>(mapStateToProps)(
    EmailCcSelectionComponent
);
